
import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import InputDate from '../../components/InputDate'
import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import moment from 'moment'
import { getExpectedEndDate } from '@/Helpers/ProgrammationHelper'
import { useProject } from '@/Hooks/UseProject'
import TaskDisplayMode from '../TaskDisplayMode'
import { useAuth } from '@/Hooks/UseAuth'

import CheckIcon from '@/Components/SvgImages/Check'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TimesIcon from '@/Components/SvgImages/Times'

import './TaskActualDates.scss'
import { updateTaskActualDates } from '@/Services/ProgrammationManagerService'
import { useDispatch } from 'react-redux'
import { updateTaskDateAction } from '@/Redux/Actions/ProgrammationManagerActions'

const TaskActualDates = () => {

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { ability } = useAuth()

  const { itemOpened, patchItemOpened, setEditingItem } = useProgrammation()
  const { project } = useProject()

  const originalStartedAt = moment(itemOpened.startedAt).format('YYYY-MM-DD')
  const originalEndedAt = itemOpened.endedAt ? moment(itemOpened.endedAt).format('YYYY-MM-DD') : ''

  const [ startedAt, setStartedAt ] = useState(originalStartedAt)
  const [ endedAt, setEndedAt ] = useState(originalEndedAt)
  const [ isEditingMode, setIsEditingMode ] = useState(false)
  const [ expectedEndDate, setExpectedEndDate ] = useState(moment(itemOpened.startedAt).format('YYYY-MM-DD'))

  const [ saving, setSaving ] = useState(false)

  const getEstimatedEndDate = (date: string) => {

    if (itemOpened.executeDays === 1) {

      setExpectedEndDate(date)
      return
    }

    const estimatedEndDate = getExpectedEndDate(date, itemOpened.executeDays, project.nonWorkingDays, false)
    setExpectedEndDate(estimatedEndDate)
  }

  const handleChangeStartedAtDate = (date: string) => {

    setStartedAt(date)

    if (!itemOpened.endedAt) {

      getEstimatedEndDate(date)
    } else {
      if (moment(date).isAfter(moment(itemOpened.endedAt))) {

        setEndedAt(itemOpened.endedAt)
      }
    }
  }

  const handleChangeEndedAtDate = (date: string) => {

    setEndedAt(date)

    if (moment(date).isBefore(moment(startedAt))) {

      setStartedAt(date)
    }
  }

  const handleCancel = async () => {

    setStartedAt(originalStartedAt)

    if (originalEndedAt) {

      setEndedAt(originalEndedAt)
    } else {

      getEstimatedEndDate(itemOpened.startedAt)
    }

    setIsEditingMode(false)
  }

  const handleSave = async () => {

    setSaving(true)
    setEditingItem(true)

    const [ error, data ] = await updateTaskActualDates(itemOpened._id, startedAt, endedAt)

    setSaving(false)
    setEditingItem(false)

    if (error) {
      return false
    }

    patchItemOpened({
      startedAt: data.task.startedAt,
      endedAt: data.task.endedAt,
    })

    await dispatch(updateTaskDateAction(data.task))
  }

  useEffect(() => {

    if (!itemOpened.endedAt) {

      getEstimatedEndDate(itemOpened.startedAt)
      setEndedAt('')
    } else {

      setEndedAt(moment(itemOpened.endedAt).format('YYYY-MM-DD'))
    }
  }, [itemOpened])

  useEffect(() => {

    if (startedAt !== originalStartedAt || (originalEndedAt && endedAt !== originalEndedAt)) {

      setIsEditingMode(true)
    } else {
      setIsEditingMode(false)
    }
  }, [startedAt, endedAt])

  useEffect(() => {

    setStartedAt(moment(itemOpened.startedAt).format('YYYY-MM-DD'))
    setIsEditingMode(false)
  }, [itemOpened])

  return (
    <Row>
      <Col sm={4}>
        <Form.Group>
          <Form.Label>
            {t('PROGRAMMATION_MANAGER:StartedAtLabel')}
          </Form.Label>
          <InputDate
            value={startedAt}
            onChange={handleChangeStartedAtDate}
            placeholder="dd/mm/yyyy"
            disabled={ability.cannot('update', 'actual-dates')}
          />
        </Form.Group>
      </Col>
      <Col sm={4}>
        <Form.Group>
          <Form.Label>
            {!itemOpened.endedAt && t('PROGRAMMATION_MANAGER:EstimatedEndDateLabel')}
            {itemOpened.endedAt &&t('PROGRAMMATION_MANAGER:EndedAtLabel')}
          </Form.Label>
          {itemOpened.endedAt && (
            <InputDate
              value={endedAt}
              onChange={handleChangeEndedAtDate}
              placeholder="dd/mm/yyyy"
              disabled={ability.cannot('update', 'actual-dates')}
            />
          )}
          {!itemOpened.endedAt && (
            <InputDate
              value={expectedEndDate}
              onChange={setExpectedEndDate}
              placeholder="dd/mm/yyyy"
              disabled
            />
          )}
        </Form.Group>
      </Col>
      {isEditingMode && (
        <Col>
          <Form.Group>
            <Form.Label>
              &nbsp;
            </Form.Label>
            <div className="save-cancel-buttons">
              <button
                className={`btn btn-save`}
                onClick={() => handleSave()}
                disabled={saving}
              >
                {saving && (
                  <Spinner
                    height={24}
                    width={24}
                    fill="#0057b7"
                  />
                )}
                {!saving && (
                  <>
                    <CheckIcon
                      height={24}
                      width={24}
                      fill="#0057b7"
                    />
                  </>
                )}
              </button>
              <button
                className={`btn btn-secondary`}
                onClick={() => handleCancel()}
                disabled={saving}
              >
                <TimesIcon height={24} width={24} />
              </button>
            </div>
          </Form.Group>
        </Col>
      )}
      {!isEditingMode && itemOpened.endedAt && (
        <Col>
          <TaskDisplayMode />
        </Col>
      )}
    </Row>
  )
}

export default TaskActualDates
